'use client'

import { useState, type ReactNode } from 'react'
import Link from 'next/link'
import { PlayIcon } from '@heroicons/react/24/solid'
import { ProductThumbnail } from '@/General/ProductThumbnail'
import { tracking } from '@/tracking'

export const TrackedHomepageProductLink = ({
  animatedPreviewSrc,
  brandCode,
  children,
  href,
  position,
  productId,
  productListId,
  productName,
  previewFallbackSrc,
}: TrackedHomepageProductLinkProps) => {
  const [isPreviewVisible, setIsPreviewVisible] = useState(false)

  const showPreview = () => {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    if (animatedPreviewSrc && !prefersReducedMotion) {
      setIsPreviewVisible(true)
    }
  }

  const trackClick = () => {
    tracking.track(
      'Clicked homepage featured product',
      {
        'Brand code': brandCode,
        Position: position,
        'Product ID': productId,
        'Product list ID': productListId,
        'Product name': productName,
        Url: href,
      },
      { destinations: ['kissmetrics'] }
    )
  }

  return (
    <Link
      href={href}
      prefetch={false}
      aria-label={`View ${productName}`}
      data-position={position}
      data-productid={productId}
      data-productlistid={productListId}
      data-animated-preview={animatedPreviewSrc || undefined}
      onBlur={() => setIsPreviewVisible(false)}
      onClick={trackClick}
      onFocus={showPreview}
      onMouseEnter={showPreview}
      onMouseLeave={() => setIsPreviewVisible(false)}
      className="group block cursor-pointer rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-vi-red"
    >
      <span className="relative block overflow-hidden rounded-sm">
        {children}
        {isPreviewVisible && animatedPreviewSrc ? (
          <ProductThumbnail
            src={animatedPreviewSrc}
            fallbackSrc={previewFallbackSrc || undefined}
            alt=""
            aria-hidden="true"
            width={640}
            height={360}
            className="absolute inset-0 aspect-video h-full w-full rounded-sm border border-gray-200 object-cover motion-reduce:hidden"
            loading="eager"
            fetchPriority="high"
          />
        ) : null}
        {animatedPreviewSrc ? (
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center bg-gradient-to-t from-black/85 via-black/50 to-transparent px-1 pb-2 pt-8 transition-all duration-200 ease-out motion-reduce:transition-none sm:px-3 sm:pb-3 sm:pt-10 ${
              isPreviewVisible
                ? 'translate-y-0 opacity-100'
                : 'translate-y-2 opacity-0'
            }`}
          >
            <span className="inline-flex max-w-full items-center gap-1 rounded-full border border-white/30 bg-black/70 px-1.5 py-1 text-white shadow-[0_8px_24px_rgba(0,0,0,0.35)] backdrop-blur-sm sm:gap-2 sm:px-2.5 sm:py-1.5">
              <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-vi-red shadow-sm sm:size-6">
                <PlayIcon
                  className="size-2.5 translate-x-px sm:size-3.5"
                  aria-hidden="true"
                />
              </span>
              <span className="whitespace-nowrap text-[9px] font-semibold tracking-wide sm:text-sm">
                <span className="sm:hidden">Trailer</span>
                <span className="hidden sm:inline">Click to watch trailer</span>
              </span>
            </span>
          </span>
        ) : null}
      </span>
    </Link>
  )
}

interface TrackedHomepageProductLinkProps {
  animatedPreviewSrc?: string | null
  brandCode: string
  children: ReactNode
  href: string
  position: number
  productId: number
  productListId: number
  productName: string
  previewFallbackSrc?: string | null
}
